/**
 * Forms, loaded as the app needs them. src/content/forms.json is the scripts' source; the app reads the
 * copy scripts/build-content.py writes to src/content/forms/, split by issuer:
 *   <issuer>.json             the issuer's forms without their history, fetched once a court the
 *                             issuer covers opens (the Judicial Council's with the first California court)
 *   <issuer>.history-<n>.json { form id: history } for the forms whose `history_part` is n, fetched when
 *                             one of them opens, and set as each one's `history`
 * `load(path)` fetches and parses one file. `order` is the issuer ids in issuers.json order, which the
 * loaded forms keep (as forms.json does), whatever order the files arrive in.
 */
export function formStore(load, order) {
  const all = []; // every form loaded so far
  const indexes = new Map(); // issuer id -> promise of its forms, once asked for
  const loaded = new Set(); // issuer ids whose forms are in `all`
  const histories = new Map(); // history file -> { state: "loading" | "done" | "failed" }
  const historyFile = (f) => `${f.issuer}.history-${f.history_part}.json`;
  const rank = (id) => { const n = order.indexOf(id); return n < 0 ? order.length : n; };
  return {
    all,
    /** Whether the forms of these issuers are loaded. */
    ready: (issuerIds) => issuerIds.every((id) => loaded.has(id)),
    /** Loads the forms of these issuers. A file that fails is fetched again on the next call. */
    load(issuerIds) {
      return Promise.all(issuerIds.map((id) => {
        if (!indexes.has(id)) {
          indexes.set(id, load(`src/content/forms/${id}.json`).then((fs) => {
            all.push(...fs);
            all.sort((a, b) => rank(a.issuer) - rank(b.issuer)); // stable, so each issuer keeps its own order
            loaded.add(id);
          }, (err) => { indexes.delete(id); throw err; }));
        }
        return indexes.get(id);
      }));
    },
    /**
     * Whether form `f`'s history is here: "ready", "loading" or "failed". The call that starts the
     * load gets `settled` called once it succeeds or fails.
     */
    history(f, settled) {
      if (f.history) return "ready";
      const file = historyFile(f), s = histories.get(file);
      if (s) return s.state === "loading" ? "loading" : "failed";
      const next = { state: "loading" };
      histories.set(file, next);
      load(`src/content/forms/${file}`).then((byId) => {
        all.forEach((g) => { if (g.issuer === f.issuer && byId[g.id]) g.history = byId[g.id]; });
        next.state = "done";
      }, () => { next.state = "failed"; }).then(settled);
      return "loading";
    },
    /** Forgets a failed history load, so the next `history` call tries again. */
    retry(f) { if ((histories.get(historyFile(f)) || {}).state !== "loading") histories.delete(historyFile(f)); },
  };
}
